import clsx from 'clsx';
import Image from 'next/image';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from '@/app/components/Modal';
import { PaginationBar } from '@/app/components/PaginationBar';
import { Spinner } from '@/app/components/Spinner';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { Search } from '@/app/components/form/Search';
import { usePagination } from '@/hooks/usePagination';
import type { GalleryItemData } from '@/types/galleryApiTypes';
import { getPaginationData } from '@/utils/getPaginationData';
import { useGetGalleryQuery } from '../../hooks/useGetGalleryQuery';
import type { SelectGalleryImageModalProps } from './types';
import { getFilePath } from './utils/getFilePath';

export const SelectGalleryImageModal = ({ isOpen, onClose, onSelect, userId }: SelectGalleryImageModalProps) => {
  const [selectedImage, setSelectedImage] = useState<GalleryItemData | null>(null);
  const {
    data: galleryImages,
    isFetching,
    isLoading,
    setSearch,
    search: searchQuery,
  } = useGetGalleryQuery({ userId, type: 'image' });
  const { handleChangePage } = usePagination(28, galleryImages?.pagination?.total || 0, 'page');

  const handleSelect = (image: GalleryItemData | null) => {
    if (!image) {
      toast.error('No image selected');
      return;
    }
    setSelectedImage(image);
    onSelect(image);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (!galleryImages) return null;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="py-[46px] px-[36px] flex flex-col w-[786px]">
        <h2 className="light text-blue-900 mb-[6px] text-left">Select image from gallery</h2>
        <div className="flex flex-col gap-5 mt-[10px]">
          <Search
            value={searchQuery}
            name="search"
            placeholder="Search name"
            className="w-full"
            onChange={handleSearchChange}
          />
          <div className="h-[402px] overflow-y-auto overflow-x-hidden pr-1.5">
            {isFetching || isLoading ? (
              <Spinner />
            ) : (
              <div className={clsx('grid-cols-7 grid-rows-4 gap-[14px]', { grid: Boolean(galleryImages.data.length) })}>
                {galleryImages.data.length > 0 ? (
                  galleryImages?.data.map((image) => (
                    <button
                      key={image.id}
                      className={clsx(
                        'w-[94px] h-[94px] cursor-pointer',
                        selectedImage?.id === image.id ? 'border-2 border-blue-500 rounded-[8px]' : ''
                      )}
                      onClick={() => setSelectedImage(image)}
                    >
                      <Image
                        src={getFilePath(image.thumbnail || image.path)}
                        alt={image.title ?? 'No image'}
                        width={90}
                        height={90}
                        className="object-cover rounded-[6px] w-[90px] h-[90px]"
                      />
                    </button>
                  ))
                ) : (
                  <div className="flex items-center justify-center mt-5 h-full w-full">
                    <p className="text-body-regular text-grey-700">No images found</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <PaginationBar
            currentPage={getPaginationData(galleryImages.pagination).currentPage}
            totalItems={getPaginationData(galleryImages.pagination).totalItems}
            totalPages={getPaginationData(galleryImages.pagination).totalPages}
            itemsPerPage={getPaginationData(galleryImages.pagination).itemsPerPage}
            onPageChange={handleChangePage}
          />
        </div>

        <div className="flex gap-[20px] w-full mt-[16px]">
          <BaseButton intent="secondary" onClick={onClose} size="full">
            Cancel
          </BaseButton>
          <BaseButton
            disabled={!selectedImage}
            intent="primary"
            onClick={() => handleSelect(selectedImage)}
            size="full"
          >
            Select Image
          </BaseButton>
        </div>
      </div>
    </Modal>
  );
};